import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@spartacus/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { CUSTOM_LAUNCH_CALLER } from 'src/app/dialog/custom-launch-dialog-config';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { CartExtendService } from 'src/app/service/cart/cart-extend.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { GoogleTagManagerEvent } from 'src/app/service/googletagmanager/google-tag-manager.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { PotentialPromotionsComponent } from '../../../potential-promotions/potential-promotions.component';
import { TooltipTextComponent } from '../../../tooltip-text/tooltip-text.component';
// tslint:disable-next-line: max-line-length
// tslint:disable-next-line: max-line-length

@Component({
  selector: 'app-saved-cart-overview',
  templateUrl: './saved-cart-overview.component.html',
  styleUrls: ['./saved-cart-overview.component.scss'],
  imports: [
    NgClass,
    TooltipTextComponent,
    PotentialPromotionsComponent,
    MatTooltip,
    MatIcon,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class SavedCartOverviewComponent implements OnInit, OnDestroy {
  cartId: string;
  order$: Observable<any>;
  srpSubscription$: Subscription;
  savedCartDetailsSubscription$: Subscription;
  showSrp = false;
  saveCartName = '';
  saveCartDescription = '';
  noItems = '';
  dateFormatted: any;
  dateFormattedView: any;
  savedCartDetailsGtmDataLayer: any;

  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();
  businessType: string = this.currentBusinesstype.getBusinessType();

  affiliation = `/${this.businessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;
  categories: any = {};

  isCustomTranslationsEnabled$: Observable<boolean> = combineLatest([this.activeCountry$, this.activeLanguage$]).pipe(
    switchMap(([activeCountry, activeLanguage]) =>
      this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, [
        'cart.orderSummary.salesTax',
        'cart.orderSummary.subtotal',
        'cart.orderSummary.total',
      ])
    )
  );

  constructor(
    private route: ActivatedRoute,
    protected cartExtendService: CartExtendService,
    private componentService: ComponentService,
    private cdr: ChangeDetectorRef,
    private baseStoreService: BaseStoreService,
    private languageService: ShimanoLanguageService,
    private countryService: ShimanoCountryService,
    private currentBusinesstype: CurrentBusinessType,
    private customTranslationService: CustomTranslationService,
    public deviceDetectorService: DeviceDetectorService,
    protected modalService: StandardModalService,
    protected el: ElementRef,
    protected vcr: ViewContainerRef,
    private storageService: StorageService
  ) {
    this.route.params.pipe(take(1)).subscribe((params) => {
      this.cartId = params.cartId;
    });
    this.baseStoreService
      .getBasestoreData()
      .pipe(take(1))
      .subscribe((baseStoreData) => {
        this.dateFormatted = baseStoreData.dateFormat.replace(/\//g, '');
        this.dateFormattedView = baseStoreData.dateFormat.replace(/\//g, '-');
      });
  }

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  ngOnInit() {
    this.order$ = this.languageService.getActive().pipe(
      switchMap(() => this.cartExtendService.getSavedCartDetails(this.cartId)),
      tap((savedCartDetails) => {
        this.saveCartName = savedCartDetails.savedCartData.name;
        this.saveCartDescription = savedCartDetails.savedCartData.description;
        this.noItems = savedCartDetails.savedCartData.totalItems;
        this.setSavedCartDetailsGtmDataLayerDetails(savedCartDetails.savedCartData);
      })
    );

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.savedCartDetailsSubscription$ = this.componentService.updateSavedCartDetails$.subscribe((status) => {
      if (status) {
        this.order$ = this.cartExtendService.getSavedCartDetails(this.cartId);
      }
    });
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  onRestoreSavedCartModal(name: string, code: string, totalItems: string) {
    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.RESTORE_SAVED_CART, this.el, this.vcr, {
        cartName: name,
        cartId: code,
        cartTotalItems: totalItems,
        dateFormatted: this.dateFormatted,
        reorder: false,
        isSavedCartDetails: true,
        savedCartDetailsGtmDataLayer: this.savedCartDetailsGtmDataLayer,
      })
      ?.pipe(take(1))
      .subscribe();
  }

  onEditCartModal(): void {
    const name = this.saveCartName;
    const desc = this.saveCartDescription;
    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.EDIT_SAVED_CART_DETAILS, this.el, this.vcr, {
        cartId: this.cartId,
        disabled: true,
        values: [
          { id: 'saveCartName', value: name },
          { id: 'saveCartDescription', value: desc },
        ],
      })
      ?.pipe(take(1))
      .subscribe();

    const onEditModalSubscription$ = this.modalService.getClosedModalData().subscribe((result) => {
      if (result !== 'close' && result !== undefined) {
        this.saveCartName = result.saveCartName || this.saveCartName;
        this.saveCartDescription = result.saveCartDescription || this.saveCartDescription;
        this.cdr.detectChanges();
      }
      onEditModalSubscription$.unsubscribe();
    });
  }

  onDeleteSavedCartModal(code: string) {
    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.DELETE_SAVED_CART, this.el, this.vcr, {
        cartId: code,
        isSavedCartDetails: true,
        cartName: this.saveCartName,
      })
      ?.pipe(take(1))
      .subscribe();
  }

  setCustomContext(isCustomTranslationsEnabled: boolean, activeCountry: string, activeLanguage: string) {
    return isCustomTranslationsEnabled ? [activeCountry, activeLanguage].join('-') : null;
  }

  setSavedCartDetailsGtmDataLayerDetails(savedCartDetails: any) {
    const productsDataLayer: any[] = [];
    savedCartDetails.entries?.forEach((entry: any) => {
      this.categories = {};
      entry.product.categories?.forEach((cat: any, i: any) => {
        const indexCount = i + 1;
        const label = `item_category${indexCount == 1 ? '' : indexCount}`;
        this.categories[label] = cat.name;
      });

      productsDataLayer.push({
        item_id: entry.product.code,
        item_name: entry.product.name,
        affiliation: this.affiliation,
        item_brand: entry?.product?.brand?.name,
        ...this.categories,
        price: entry.basePrice?.value,
        quantity: entry.quantity,
      });
    });
    const gtmTag = {
      event: GoogleTagManagerEvent.ADD_TO_CART,
      ecommerce: {
        currency: savedCartDetails.totalPrice.currencyIso,
        value: savedCartDetails.totalPrice.value,
        items: productsDataLayer,
      },
    };
    this.savedCartDetailsGtmDataLayer = gtmTag;
  }

  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.savedCartDetailsSubscription$) {
      this.savedCartDetailsSubscription$.unsubscribe();
    }
  }
}
